/**
 * ImmunizationsChartTab — Immunizations inner tab of the patient Chart
 * shell.
 *
 * Flutter ref: `patient_chart/.../tab_widgets/vax_tab.dart` ("Vax" is
 * Flutter's shorthand; we use the longer-form "Immunizations" label per
 * Signal DS verbiage rules).
 *
 * FHIR: `Immunization R4`. One row per resource.
 *
 * **Add / Edit / Delete (PROD-827)**: shipped 2026-05-20. The same
 * `AddImmunizationEditor` powers both Add and Edit (per Flutter's
 * `vax_tab.dart` inline editor pattern). Delete is a soft-delete via
 * PUT `/immunization/{id}` with `isDeleted: true`. Strict `performer`
 * audit guard refuses to ship if the original recorder is missing —
 * same pattern as Allergies' `recorder` and Procedures' `asserter`.
 */

import { useReportChartCount } from './chart-page-context';
import { ChartShowAll } from './chart-row-cap';
import { useRowCap } from './use-row-cap';
import { useState } from 'react';
import { toast } from 'sonner';
import { ModalYesNo } from '@scripsteam/scrips-signal-ds';
import { Skeleton } from '@scripsteam/scrips-signal-ds';
import { usePatientImmunizations } from '@/patients/services/use-patient-immunizations';
import { usePatient } from '@/patients/services/use-patient';
import type { Patient } from '@/types/patient-api';
import { useUpdateImmunization } from '@/patients/services/use-update-immunization';
import { ChartSectionHeader } from './chart-section-header';
import { ChartRowActions } from '@scripsteam/scrips-signal-ds';
import { AddImmunizationEditor } from './add-immunization-editor';
import { normalizePerformerDetails } from './add-allergy-dialog';
import {
  DATA_ABSENT_REASON_SENTINEL,
  STATUS_REASON_SENTINEL,
} from '@/patients/lib/chart-wire-literals';
import { VaccineTimelineDialog } from './vaccine-timeline-dialog';
import type {
  ImmunizationCoding,
  PatientImmunization,
  UpdateImmunizationBody,
} from '@/types/patient-chart-api';
import type { ChartCell } from './chart-table-v2';
import { EmptyState } from '@scripsteam/scrips-signal-ds';

export interface ImmunizationsChartTabProps {
  patientId: string | null | undefined;
  /** The patient the chart host already loaded. With it this tab asks for nothing: a second `usePatient` observer
   *  mounting while the first is stale refetched the same patient (the one-page chart mounts every section at once). */
  patient?: Patient | null;
}

export function ImmunizationsChartTab({
  patientId,
  patient: loaded,
}: ImmunizationsChartTabProps) {
  const { data, isLoading, isError, refetch } = usePatientImmunizations(patientId);
  const { data: fetched } = usePatient(loaded ? null : patientId);
  const patient = loaded ?? fetched;
  const updateMut = useUpdateImmunization();
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<PatientImmunization | null>(null);
  const [deleting, setDeleting] = useState<PatientImmunization | null>(null);
  const rows = (data ?? []).filter((v) => v.isDeleted !== true);
  useReportChartCount(isLoading ? null : rows.length);
  const cap = useRowCap(rows);

  const editorOpen = addOpen || editing !== null;

  const closeEditor = () => {
    setAddOpen(false);
    setEditing(null);
  };
  const startEdit = (v: PatientImmunization) => {
    setAddOpen(false);
    setEditing(v);
  };

  // Soft-delete via PUT /immunization/{id} with isDeleted: true.
  // Strict performer audit guard — refuses to ship if the original
  // recorder is missing (same pattern as Allergies/Procedures).
  //
  // **UX-visible failure mode**: toast.error fires via sonner,
  // mutation NOT called, AlertDialog stays open with the user able
  // to dismiss. No silent no-op.
  const confirmDelete = async () => {
    if (!deleting?.identifier || !patientId) return;
    const v = deleting;
    if (!v.performer) {
      toast.error(
        'Cannot delete — this record is missing its original recorder. Please contact support.',
      );
      return;
    }
    const body: UpdateImmunizationBody = {
      identifier: v.identifier!,
      vaccine: normalizeCodingFromRead(v.vaccine),
      status: 'Active',
      patient: v.patient ?? patientId,
      encounter: v.encounter ?? null,
      dataAbsentReason: DATA_ABSENT_REASON_SENTINEL,
      performer: v.performer,
      expiryDisplayDate: v.expiryDisplayDate ?? null,
      administratedDisplayDate: v.administratedDisplayDate ?? null,
      administratedDate: v.administratedDate ?? null,
      expiryDate: v.expiryDate ?? null,
      quantity: {
        value: v.quantity?.value ?? null,
        unit: v.quantity?.unit ?? null,
        system: v.quantity?.system ?? null,
        code: v.quantity?.code ?? null,
      },
      statusReason: STATUS_REASON_SENTINEL,
      site: normalizeCodingFromRead(v.site),
      remarks: v.remarks ?? '',
      lotNo: v.lotNo ?? null,
      isDeleted: true,
      performerDetails: normalizePerformerDetails(v.performerDetails),
      createdDate: v.createdDate ?? null,
      isRx: v.isRx === true,
      durationQuantity: normalizeCodingFromRead(v.durationQuantity),
      durationUnits: normalizeCodingFromRead(v.durationUnits),
      speciality: null,
    };
    try {
      await updateMut.mutateAsync({
        patientId,
        immunizationId: v.identifier!,
        body,
      });
      toast.success(
        `Removed immunization: ${(v.vaccine?.display ?? 'Unknown').trim()}`,
      );
      setDeleting(null);
    } catch {
      /* Global toast surfaces failure; keep dialog open. */
    }
  };

  if (!patientId) {
    return (
      <div className="p-6">
        <EmptyState icon="info" title="Save the patient first to record immunizations." />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6">
        <EmptyState
          icon="warning"
          title="Couldn't load immunizations."
          ctaLabel="Retry"
          onCtaClick={() => void refetch()}
        />
      </div>
    );
  }

  // The design's empty screen (chart-vaccinations-empty.html): the section's add on its title row and the DS
  // EmptyState (syringe, the one line, "Add immunization") under it, with the switches ON or OFF.
  if (rows.length === 0) {
    return (
      <div className="p-6 space-y-4">
        <ChartSectionHeader
          title="Immunizations"
          count={0}
          onAdd={addOpen ? undefined : () => setAddOpen(true)}
        />
        {addOpen ? (
          <AddImmunizationEditor patientId={patientId} onClose={closeEditor} />
        ) : (
          <EmptyState
            icon="syringe"
            title="No immunizations recorded for this patient."
            ctaLabel="Add immunization"
            onCtaClick={() => setAddOpen(true)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-3">
      <ChartSectionHeader
        title="Immunizations"
        count={rows.length}
        onAdd={editorOpen ? undefined : () => setAddOpen(true)}
        extra={
          <button
            type="button"
            onClick={() => setTimelineOpen(true)}
            data-slot="chart-section-link"
            className="text-sm font-semibold text-brand-primary hover:underline"
          >
            Vaccine chart
          </button>
        }
      />
      {editorOpen ? (
        <AddImmunizationEditor
          key={editing?.identifier ?? 'add'}
          patientId={patientId}
          editing={editing}
          onClose={closeEditor}
        />
      ) : null}
      <VaccineTimelineDialog
        open={timelineOpen}
        onOpenChange={setTimelineOpen}
        patient={patient ?? null}
      />
      <div className="rounded-lg border border-border-default overflow-hidden bg-surface-card">
        <table className="w-full text-sm">
          <thead className="bg-grey-100">
            <tr className="text-left text-text-muted">
              <Th>Vaccine</Th>
              <Th>Administered</Th>
              <Th>Expiry</Th>
              <Th>Lot #</Th>
              <Th>Site</Th>
              <Th>Performer</Th>
              <Th>Remarks</Th>
              <Th align="right" label="Actions" />
            </tr>
          </thead>
          <tbody>
            {cap.visible.map((v, i) => (
              <tr
                key={v.identifier ?? `${i}-${v.vaccine?.code ?? 'unknown'}`}
                className="border-t border-border-default hover:bg-grey-100/50"
              >
                <Td cell="name">
                  <span>{v.vaccine?.display ?? '—'}</span>
                  {immunizationSubLine(v) ? <div className="text-xs text-text-hint">{immunizationSubLine(v)}</div> : null}
                </Td>
                <Td date>
                  {formatImmunizationDate(v.administratedDate, v.administratedDisplayDate)}
                </Td>
                <Td date>{formatImmunizationDate(v.expiryDate, v.expiryDisplayDate)}</Td>
                <Td cell="text">{quiet(v.lotNo?.trim())}</Td>
                <Td cell="text">{quiet(v.site?.display)}</Td>
                <Td cell="text">{quiet(v.performerDetails?.name)}</Td>
                <Td cell="note" tip={v.remarks?.trim() || '—'}>{quiet(v.remarks?.trim())}</Td>
                <Td align="right" cell="actions">
                  <ChartRowActions
                    editLabel={`Edit immunization: ${v.vaccine?.display ?? 'Unknown'}`}
                    deleteLabel={`Delete immunization: ${v.vaccine?.display ?? 'Unknown'}`}
                    onEdit={editorOpen ? undefined : () => startEdit(v)}
                    onDelete={editorOpen ? undefined : () => setDeleting(v)}
                  />
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ChartShowAll cap={cap} label="vaccinations" />

      <ModalYesNo
      open={deleting !== null}
      onCancel={() => setTimelineOpen(false)}
      title="Are you sure?"
      message={<>Do you want to proceed on deleting{' '}
              <span className="font-semibold text-brand-depth">
                {(deleting?.vaccine?.display ?? 'this immunization').trim()}
              </span>
              ?</>}
      cancelLabel="Cancel"
      confirmLabel={updateMut.isPending ? 'Deleting…' : 'Delete'}
      onConfirm={() => {
                void confirmDelete();
              }}
      loading={updateMut.isPending}
    />
    </div>
  );
}

/**
 * Normalize a read-side `CodeWordCase` (5-field) into the wire-side
 * `ImmunizationCoding` (6-field with PascalCase `Definition`). Used on
 * the soft-delete path to round-trip the original record's Coding
 * fields without losing the wire-shape contract. Edit-mode submissions
 * go through the editor's own normalizer.
 */
function normalizeCodingFromRead(
  row: { code?: string | null; display?: string | null; system?: string | null; description?: string | null; shortName?: string | null } | null | undefined,
): ImmunizationCoding | null {
  if (!row) return null;
  return {
    code: row.code ?? null,
    // Definition isn't surfaced on the read-side type; default null —
    // the wire ships null on this field for almost every Coding (curl-
    // verified). If a future record's Definition is non-null on read,
    // a wire-shape doc update under PROD-842 will land in lockstep.
    Definition: null,
    display: row.display ?? null,
    system: row.system ?? null,
    description: row.description ?? null,
    shortName: row.shortName ?? null,
  };
}

/** A header cell in the design's look (sentence case, label weight), with the switches ON or OFF. A header with
 *  `label` and no text is named for assistive tech only: the design's actions column has no visible header. */
function Th({
  children,
  align = 'left',
  label,
}: {
  children?: React.ReactNode;
  align?: 'left' | 'right';
  label?: string;
}) {
  return (
    <th
      aria-label={label}
      className={`px-3 py-2 text-xs font-medium ${align === 'right' ? 'text-right' : 'text-left'}`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = 'left',
  date = false,
  cell,
  tip,
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
  /** What the column holds (the bridge sizes it: index, name, text, short, date, note, actions). */
  cell?: ChartCell;
  /** A note's full text for the app's instant tooltip, shown when the one-line cell is cut off. */
  tip?: string;
  /** A date: kept on one line (the bridge's "patient chart" block, `data-chart-cell="date"`). */
  date?: boolean;
}) {
  return (
    <td
      data-chart-cell={date ? 'date' : cell}
      data-tip={tip}
      className={`px-3 py-2 align-top ${align === 'right' ? 'text-right' : 'text-left'}`}
    >
      {children}
    </td>
  );
}

/** An absent value: the dash in the meta ink (design ChartRecordTable `.quiet`; the immunizations section of
 *  showcase-patient.css). */
function quiet(value: string | null | undefined) {
  return value ? value : <span data-slot="immunization-quiet">—</span>;
}

/** The name's sub-line (design ImmunizationTable: "CVX 141 · 0.5 mL"): the vaccine's code, then its dose. The code is
 *  labelled CVX only when the record's own coding system is CVX (a system is never guessed: PROD-2851); otherwise it
 *  shows bare, as before. Either part may be absent; with neither there is no sub-line. */
function immunizationSubLine(v: PatientImmunization): string {
  const code = v.vaccine?.code?.trim();
  const codePart = code ? (/cvx/i.test(v.vaccine?.system ?? '') ? `CVX ${code}` : code) : '';
  return [codePart, doseLabel(v)].filter(Boolean).join(' · ');
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** The design's date, "02 Oct 2025" (two-digit day, short month, year), built from the date's own local parts, not
 *  `toLocale*` (an en-US locale gave "Oct 02, 2025", and newer ICU spells September "Sept"). Presentation only. */
function formatImmunizationDate(
  iso: string | null | undefined,
  displayFallback?: string | null,
): string {
  if (iso && iso !== 'n/a') {
    const d = new Date(iso);
    if (!Number.isNaN(d.getTime())) {
      return `${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
    }
  }
  if (displayFallback?.trim()) return displayFallback;
  return '—';
}

/** The dose, for the sub-line now that it has no column of its own. `durationQuantity.display` is the dose's place in
 *  the series (Flutter PatientImmunization: "1", "2", "Other", "Unknown"), so it is named "Dose 1" there (the word the
 *  removed column header carried); the legacy `quantity` (null on the wire, 2026-06-09 probe) is an amount, "0.5 mL".
 *  No dose → ''. */
function doseLabel(v: PatientImmunization): string {
  const disp = v.durationQuantity?.display?.trim();
  if (disp) return /^dose/i.test(disp) ? disp : `Dose ${disp}`;
  const q = v.quantity;
  if (q && q.value != null) return `${q.value}${q.unit ? ` ${q.unit}` : ''}`;
  return '';
}
